import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";

export default function Cart() {
  const { items, remove, setQty } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({}); // product_id -> {shipping_price, free_shipping, allow_pickup, pickup_city, pickup_country}
  const [modes, setModes] = useState({}); // "product_id|opts" -> "ship" | "pickup"
  const [buyerName, setBuyerName] = useState("");
  const [ship, setShip] = useState({
    name: "", line1: "", line2: "", city: "", state: "",
    postal_code: "", country: "", phone: "",
  });
  const hasTicket = items.some((i) => i.category === "ticket");

  // Fetch each physical product's shipping metadata
  useEffect(() => {
    const uniquePhysicalIds = [...new Set(items.filter((i) => i.type === "physical").map((i) => i.product_id))];
    Promise.all(uniquePhysicalIds.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null)))
      .then((results) => {
        const m = {};
        for (const p of results) {
          if (p) {
            m[p.product_id] = {
              shipping_price: p.shipping_price,
              free_shipping: p.free_shipping,
              allow_pickup: p.allow_pickup,
              pickup_city: p.pickup_city,
              pickup_country: p.pickup_country,
            };
          }
        }
        setMeta(m);
      });
  }, [items]);

  const getMode = (i, optsKey) => modes[`${i.product_id}|${optsKey}`] || "ship";
  const setMode = (i, optsKey, mode) => setModes((m) => ({ ...m, [`${i.product_id}|${optsKey}`]: mode }));

  const shipFeeFor = (i, optsKey) => {
    if (i.type !== "physical") return 0;
    const m = meta[i.product_id];
    if (!m) return 0;
    const mode = getMode(i, optsKey);
    if (mode === "pickup") return 0;
    if (m.free_shipping) return 0;
    return parseFloat(m.shipping_price || 0);
  };

  // Determine if any item still needs a shipping address
  const anyShipping = items.some((i) => {
    if (i.type !== "physical") return false;
    const optsKey = `${i.selected_date || ""}|${i.selected_size || ""}|${i.selected_color || ""}`;
    return getMode(i, optsKey) === "ship";
  });

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const shippingTotal = items.reduce((sum, i) => {
    const optsKey = `${i.selected_date || ""}|${i.selected_size || ""}|${i.selected_color || ""}`;
    return sum + shipFeeFor(i, optsKey);
  }, 0);
  const total = subtotal + shippingTotal;

  const checkout = async () => {
    if (!user) { toast.info("Sign in to checkout"); nav("/login"); return; }
    if (anyShipping) {
      const req = ["name", "line1", "city", "postal_code", "country"];
      for (const k of req) if (!ship[k]?.trim()) { toast.error(`Shipping ${k.replace("_", " ")} is required`); return; }
    }
    if (hasTicket && !buyerName.trim()) { toast.error("Full name is required for tickets"); return; }
    setBusy(true);
    try {
      const payload = {
        items: items.map((i) => {
          const optsKey = `${i.selected_date || ""}|${i.selected_size || ""}|${i.selected_color || ""}`;
          return {
            product_id: i.product_id,
            quantity: i.quantity,
            selected_date: i.selected_date || null,
            selected_size: i.selected_size || null,
            selected_color: i.selected_color || null,
            delivery_mode: i.type === "physical" ? getMode(i, optsKey) : null,
          };
        }),
        origin_url: window.location.origin,
        shipping_address: anyShipping ? ship : null,
        buyer_full_name: hasTicket ? buyerName.trim() : null,
      };
      const { data } = await api.post("/checkout", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-[1100px] mx-auto px-6 py-12">
        <h1 className="heading text-4xl md:text-6xl font-black uppercase tracking-tight">Cart</h1>
        {items.length === 0 ? (
          <div className="mt-8 brut-border bg-white p-12 text-center">
            <p className="mono uppercase text-sm tracking-[0.2em] text-[#555]">Your cart is empty.</p>
            <Link to="/" className="btn-primary mt-6 inline-block">Browse the store →</Link>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 brut-border bg-white">
              {items.map((i, idx) => {
                const optsKey = `${i.selected_date || ""}|${i.selected_size || ""}|${i.selected_color || ""}`;
                const metaRow = i.type === "physical" ? meta[i.product_id] : null;
                const mode = getMode(i, optsKey);
                const metaLine = [i.category === "ticket" ? "ticket" : i.type, i.selected_date, i.selected_size, i.selected_color].filter(Boolean).join(" · ");
                const fee = shipFeeFor(i, optsKey);
                return (
                <div key={`${i.product_id}-${optsKey}`} data-testid={`cart-item-${i.product_id}`} className={`p-4 ${idx > 0 ? "border-t-2 border-[#1F3D2A]" : ""}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 brut-border bg-[#EAEAEA] overflow-hidden flex-shrink-0">
                      {i.image_url && <img src={i.image_url} alt={i.title} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="heading font-bold truncate">{i.title}</div>
                      <div className="mono text-xs uppercase tracking-[0.2em] text-[#555]">{metaLine}</div>
                    </div>
                    <div className="flex items-center brut-border">
                      <button onClick={() => setQty(i.product_id, i.quantity - 1, optsKey)} className="px-3 py-1 hover:bg-[#1F3D2A] hover:text-white">−</button>
                      <span className="px-3 mono">{i.quantity}</span>
                      <button onClick={() => setQty(i.product_id, i.quantity + 1, optsKey)} className="px-3 py-1 hover:bg-[#1F3D2A] hover:text-white">+</button>
                    </div>
                    <div className="mono font-bold w-24 text-right">${(i.price * i.quantity).toFixed(2)}</div>
                    <button data-testid={`remove-${i.product_id}`} onClick={() => remove(i.product_id, optsKey)} className="brut-border bg-white p-2 hover:bg-[#C77E3C] hover:text-white"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  {i.type === "physical" && metaRow && (
                    <div className="mt-3 pl-24">
                      <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C] mb-2">/ Delivery</div>
                      {metaRow.free_shipping ? (
                        <div data-testid={`shipping-free-${i.product_id}`} className="mono text-xs uppercase tracking-[0.2em] text-[#1F3D2A] brut-border bg-[#FDF3E9] px-3 py-2 inline-block">Free shipping — item will be shipped to you</div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          <button data-testid={`ship-mode-ship-${i.product_id}`} type="button" onClick={() => setMode(i, optsKey, "ship")}
                            className={`brut-border px-3 py-2 mono text-[10px] uppercase tracking-[0.2em] ${mode === "ship" ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>
                            Ship — ${parseFloat(metaRow.shipping_price || 0).toFixed(2)}{metaRow.shipping_vat_included ? " (incl. VAT)" : ""}
                          </button>
                          {metaRow.allow_pickup && (
                            <button data-testid={`ship-mode-pickup-${i.product_id}`} type="button" onClick={() => setMode(i, optsKey, "pickup")}
                              className={`brut-border px-3 py-2 mono text-[10px] uppercase tracking-[0.2em] ${mode === "pickup" ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>
                              Local pickup ({metaRow.pickup_city || "—"}, {metaRow.pickup_country || "—"}) — Free
                            </button>
                          )}
                        </div>
                      )}
                      {i.type === "physical" && !metaRow.free_shipping && (
                        <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555] mt-2">
                          {mode === "pickup" ? "Collect from seller — no shipping fee" : `Shipping fee: $${fee.toFixed(2)}`}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );})}
            </div>
            <div className="lg:col-span-1 space-y-6">
              {hasTicket && (
                <div className="brut-border bg-[#FDF3E9] p-5">
                  <div className="mono text-xs uppercase tracking-[0.3em] text-[#C77E3C]">Full name on ticket</div>
                  <div className="text-xs text-[#555] mt-1">Required — this name will appear on every ticket in your order.</div>
                  <input data-testid="buyer-full-name" placeholder="Full name" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} className="w-full mt-3 brut-border bg-white px-3 py-2 text-sm" />
                </div>
              )}
              {anyShipping && (
                <div className="brut-border bg-white p-5">
                  <div className="mono text-xs uppercase tracking-[0.3em] text-[#C77E3C]">Shipping address</div>
                  <div className="text-xs text-[#555] mt-1">Required — at least one item is being shipped to you.</div>
                  <div className="mt-3 space-y-2">
                    {[
                      ["name", "Full name *"], ["line1", "Address line 1 *"],
                      ["line2", "Address line 2"], ["city", "City *"],
                      ["state", "State / region"], ["postal_code", "Postal code *"],
                      ["country", "Country *"], ["phone", "Phone"],
                    ].map(([k, lbl]) => (
                      <input key={k} data-testid={`ship-${k}`} placeholder={lbl}
                        value={ship[k]} onChange={(e) => setShip({ ...ship, [k]: e.target.value })}
                        className="w-full brut-border bg-white px-3 py-2 text-sm" />
                    ))}
                  </div>
                </div>
              )}
              <div className="brut-border bg-[#1F3D2A] text-white p-6 h-fit">
                <div className="mono text-xs uppercase tracking-[0.3em] text-[#C77E3C]">Summary</div>
                <div className="mt-4 flex justify-between text-sm"><span>Subtotal</span><span className="mono">${subtotal.toFixed(2)}</span></div>
                <div className="mt-2 flex justify-between text-sm"><span>Shipping</span><span data-testid="cart-shipping" className="mono">${shippingTotal.toFixed(2)}</span></div>
                <div className="mt-6 pt-4 border-t-2 border-white/20 flex justify-between heading text-2xl font-bold"><span>Total</span><span data-testid="cart-total">${total.toFixed(2)}</span></div>
                <button data-testid="checkout-btn" disabled={busy} onClick={checkout} className="btn-primary w-full mt-6">{busy ? "Redirecting…" : "Checkout"}</button>
                <p className="mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-4">Stripe handles cards, PayPal, mobile money where supported.</p>
              </div>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}
